import React from "react"
import { useTranslation } from "react-i18next"

import { getFileNameWithoutExtension } from "shared/lib/file/getFileExtension"
import { ConfirmationOverlay } from "shared/ui/confirmation-overlay/ConfirmationOverlay"
import { FileCard } from "shared/ui/file-card/FileCard"

import { zodResolver } from "@hookform/resolvers/zod"
import { LinkedEntitiesList } from "entities/attachments/ui/linked-attachments/LinkedEntitiesList"
import z from "zod"

import { ReplaceAttachmentOverlayViewModel } from "./model/useReplaceAttachmentOverlayViewModel"
import "./styles/_replace-attachment-overlay.scss"
import { ReplaceAttachmentOverlayConfirmStep } from "./ui/ReplaceAttachmentOverlayConfirmStep"
import { ReplaceAttachmentOverlayNewFileStep } from "./ui/ReplaceAttachmentOverlayNewFileStep"

const FORM_ID = "replace-attachment-confirmation-form"

const ReplaceAttachmentSchema = z.object({
  isConfirmed: z.literal(true)
})

type ReplaceAttachmentFormData = {
  isConfirmed: boolean
}

const defaultFormValues: ReplaceAttachmentFormData = {
  isConfirmed: false
}

const resolver = zodResolver<
  ReplaceAttachmentFormData,
  unknown,
  z.infer<typeof ReplaceAttachmentSchema>
>(ReplaceAttachmentSchema)

export const ReplaceAttachmentOverlayView = React.memo(
  ({
    existingFile,
    newFile,
    linkedEntities,
    isLoading,
    onUpload,
    onConfirm,
    onClose
  }: ReplaceAttachmentOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <ConfirmationOverlay.Root
        id="replace-attachment-confirmation-overlay"
        className="replace-attachment-confirmation-overlay"
        classNamePrefix="replace-attachment-confirmation-overlay"
        isLoading={isLoading}
      >
        <ConfirmationOverlay.Header>
          <ConfirmationOverlay.Title>
            {t("Attachments.ReplaceOverlay.Title")}
          </ConfirmationOverlay.Title>
          <ConfirmationOverlay.Close onClose={onClose} />
        </ConfirmationOverlay.Header>
        <ConfirmationOverlay.Content>
          <ConfirmationOverlay.Message className="replace-attachment-confirmation-overlay">
            <p className="replace-attachment-overlay__description">
              {t("Attachments.ReplaceOverlay.Description")}
            </p>
            <div className="replace-attachment-overlay__current-file">
              <span className="replace-attachment-overlay__section-label label label--bold">
                {t("Attachments.ReplaceOverlay.CurrentFile")}
              </span>
              <FileCard
                name={getFileNameWithoutExtension(existingFile.fileName)}
                extension={existingFile.extension ?? ""}
              />
            </div>
            <LinkedEntitiesList
              entities={linkedEntities}
              buttonText={t(
                "Attachments.ReplaceOverlay.AttachmentLinksPreserved"
              )}
            />
          </ConfirmationOverlay.Message>
          <ConfirmationOverlay.Form<
            ReplaceAttachmentFormData,
            z.infer<typeof ReplaceAttachmentSchema>
          >
            id={FORM_ID}
            resolver={resolver}
            defaultValues={defaultFormValues}
            onSubmit={onConfirm}
          >
            <ReplaceAttachmentOverlayConfirmStep
              fileName={existingFile.fileName}
              disabled={isLoading || newFile !== null}
            />
            <ReplaceAttachmentOverlayNewFileStep
              newFile={newFile}
              onUpload={onUpload}
            />
            {newFile === null ? null : (
              <ConfirmationOverlay.Actions>
                <ConfirmationOverlay.Submit form={FORM_ID}>
                  {t("General.Close")}
                </ConfirmationOverlay.Submit>
              </ConfirmationOverlay.Actions>
            )}
          </ConfirmationOverlay.Form>
        </ConfirmationOverlay.Content>
      </ConfirmationOverlay.Root>
    )
  }
)
